import React, { useCallback, useState } from "react";

import {
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import CustomInput from "Components/Custom/CustomInput";
import CustomButton from "Components/Custom/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "Components/Hooks/hooks";
import { addSection, editSection } from "slices/thunks";
import { SectionPayloadData } from "types/section.api";
import { updateSectionLocally } from "slices/section/reducer";
import ReactQuill from "react-quill";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
} from "Components/constants/validation";

interface ManageSectionModalProps {
  showModal: boolean;
  toggle: () => void;
  isEdit: boolean;
  existingSectionData?: SectionPayloadData | null;
  isFromQuote?: boolean;
}

const ManageSectionModal: React.FC<ManageSectionModalProps> = ({
  showModal,
  toggle,
  isEdit,
  existingSectionData,
  isFromQuote,
}) => {
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: isEdit && existingSectionData ? existingSectionData.id : 0,
      order:
        isEdit && existingSectionData ? String(existingSectionData.sort) : "",
      sectionTitle:
        isEdit && existingSectionData ? existingSectionData.title : "",
      description:
        isEdit && existingSectionData ? existingSectionData.content : "",
    },

    validationSchema: Yup.object({
      order: Yup.number()
        .typeError("Order must be a number")
        .integer("Order must be a whole number")
        .required("Order is required")
        .min(1, "Order must be at least 1")
        .max(
          VALIDATION_LIMITS.ORDER.MAX,
          `Order must be at most ${VALIDATION_LIMITS.ORDER.MAX}`,
        ),

      sectionTitle: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("section title"))
        .max(
          VALIDATION_LIMITS.TITLE.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Section title",
            VALIDATION_LIMITS.TITLE.MAX,
          ),
        ),
      description: Yup.string()
        .test(
          "is-not-empty",
          VALIDATION_MESSAGES.REQUIRED("description"),
          (value) => {
            if (!value) return false;

            const plainText = value
              .replace(/<(.|\n)*?>/g, "")
              .replace(/&nbsp;/g, "")
              .trim();

            return plainText.length > 0;
          },
        )
        .max(
          VALIDATION_LIMITS.DESCRIPTION.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Description",
            VALIDATION_LIMITS.DESCRIPTION.MAX,
          ),
        ),
    }),

    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        if (isEdit) {
          if (isFromQuote) {
            dispatch(
              updateSectionLocally({
                sectionId: values.id,
                updates: {
                  title: values.sectionTitle,
                  content: values.description,
                  sort: Number(values.order),
                },
              }),
            );
          } else {
            const updatedSection = {
              id: values.id,
              sort: Number(values.order),
              title: values.sectionTitle,
              content: values.description,
            };
            await dispatch(editSection(updatedSection))?.unwrap();
          }
        } else {
          const newSection = {
            sort: Number(values.order),
            title: values.sectionTitle,
            content: values.description,
          };
          await dispatch(addSection(newSection))?.unwrap();
        }
        validation.resetForm();
        toggle();
      } catch (error) {
        console.error("Error managing section:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleToggle = useCallback(() => {
    toggle?.();
    validation.resetForm();
  }, [toggle, validation]);

  return (
    <Modal
      id="showModal"
      isOpen={showModal}
      toggle={handleToggle}
      centered
      contentClassName="br-7"
    >
      <ModalHeader className="bg-greyF5 p-3 bg-light" toggle={handleToggle}>
        {!!isEdit ? "Edit Section" : "Add Section"}
      </ModalHeader>

      <form
        className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
      >
        <ModalBody>
          <Input type="hidden" id="id-field" />
          <Row className="g-3">
            {/* Order + Section Title */}
            <Col lg={12}>
              <Row className="align-items-start">
                {" "}
                {/* removed d-flex */}
                <Col lg={4}>
                  <CustomInput
                    label="Order"
                    type="text"
                    name="order"
                    placeholder="Order"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.order}
                    dropdownOptions={[]}
                    error={validation.errors.order}
                    touched={validation.touched.order}
                  />
                </Col>
                <Col lg={8}>
                  <CustomInput
                    label="Section Title"
                    type="text"
                    name="sectionTitle"
                    placeholder="Introduction"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.sectionTitle}
                    error={validation.errors.sectionTitle}
                    touched={validation.touched.sectionTitle}
                  />
                </Col>
              </Row>
            </Col>

            {/* Description */}
            <Col lg={12} className="mt-2">
              <Label
                htmlFor="description-field"
                className="font-primary fw-normal fs-14 text-black2D mb-1"
              >
                Description
              </Label>
              {/* 
              <Input
                type="textarea"
                className={`auth-form-control w-100 p-2 border-border ${
                  validation.touched.description &&
                  validation.errors.description
                    ? "is-invalid"
                    : ""
                }`}
                rows={10}
                name="description"
                id="description-field"
                placeholder="Enter Description"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.description}
              /> */}

              <ReactQuill
                theme="snow"
                id="description-field"
                value={validation.values.description}
                onChange={(value) =>
                  validation.setFieldValue("description", value)
                }
                onBlur={() => validation.setFieldTouched("description", true)}
                className={
                  validation.touched.description &&
                  validation.errors.description
                    ? "is-invalid"
                    : ""
                }
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ list: "bullet" }, { list: "ordered" }],
                  ],
                }}
              />

              {validation.touched.description &&
                validation.errors.description && (
                  <FormFeedback className="d-block">
                    {validation.errors.description}
                  </FormFeedback>
                )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-start">
          <CustomButton
            text={`${isEdit ? "Save Changes" : "Add Section"}`}
            className="bg-primary br-7"
            type="submit"
            loading={isSubmitting}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default React.memo(ManageSectionModal);
